// components/create-station/step4/step4.component.jsx
import React, { useMemo } from "react";
import {
  BsArrowDown,
  BsArrowUp,
  BsPlusLg,
  BsTrash,
} from "react-icons/bs";
import { FaSeedling } from "react-icons/fa";

import "./step4.styles.scss";

const MIN_PRIORITIES = 2;

const createPriorityId = () =>
  `priority-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const getPriorityId = (priority) =>
  priority.id || `priority-${priority.value}`;

const renumberPriorities = (priorities) =>
  priorities.map((priority, index) => ({
    ...priority,
    id: getPriorityId(priority),
    value: priorities.length - index,
  }));

const Step4 = ({ priorities, setPriorities }) => {
  const orderedPriorities = useMemo(
    () =>
      [...(priorities || [])]
        .sort((a, b) => b.value - a.value)
        .map((priority) => ({
          ...priority,
          id: getPriorityId(priority),
        })),
    [priorities],
  );

  const updatePriority = (id, changes) => {
    setPriorities((previous) =>
      previous.map((priority) => {
        const priorityId = getPriorityId(priority);

        return priorityId === id
          ? {
              ...priority,
              id: priorityId,
              ...changes,
            }
          : priority;
      }),
    );
  };

  const addPriority = () => {
    const nextPriorities = [
      ...orderedPriorities,
      {
        id: createPriorityId(),
        value: 0,
        name: "New priority",
        color: "#34B5E4",
      },
    ];

    setPriorities(renumberPriorities(nextPriorities));
  };

  const removePriority = (id) => {
    if (orderedPriorities.length <= MIN_PRIORITIES) return;

    setPriorities(
      renumberPriorities(
        orderedPriorities.filter(
          (priority) => priority.id !== id,
        ),
      ),
    );
  };

  const movePriority = (index, direction) => {
    const targetIndex = index + direction;

    if (
      targetIndex < 0 ||
      targetIndex >= orderedPriorities.length
    ) {
      return;
    }

    const reordered = [...orderedPriorities];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);

    setPriorities(renumberPriorities(reordered));
  };

  return (
    <div className="priorityStep">
      <h2>Customize priorities</h2>

      <p>
        Choose the names, colors, and order used by tasks in this
        project.
      </p>

      <div className="priorityStep__list">
        {orderedPriorities.map((priority, index) => (
          <div className="priorityStep__row" key={priority.id}>
            <label
              className="priorityStep__color"
              style={{ backgroundColor: priority.color }}
            >
              <FaSeedling />

              <input
                type="color"
                value={priority.color}
                onChange={(event) =>
                  updatePriority(priority.id, {
                    color: event.target.value,
                  })
                }
              />
            </label>

            <div className="priorityStep__input">
              <input
                value={priority.name}
                placeholder="Priority name"
                onChange={(event) =>
                  updatePriority(priority.id, {
                    name: event.target.value,
                  })
                }
                onBlur={() => {
                  if (!priority.name.trim()) {
                    updatePriority(priority.id, {
                      name: `Priority ${priority.value}`,
                    });
                  }
                }}
              />

              <span>{priority.value}</span>
            </div>

            <button
              type="button"
              disabled={index === 0}
              onClick={() => movePriority(index, -1)}
            >
              <BsArrowUp />
            </button>

            <button
              type="button"
              disabled={index === orderedPriorities.length - 1}
              onClick={() => movePriority(index, 1)}
            >
              <BsArrowDown />
            </button>

            <button
              type="button"
              disabled={
                orderedPriorities.length <= MIN_PRIORITIES
              }
              onClick={() => removePriority(priority.id)}
            >
              <BsTrash />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="priorityStep__add"
        onClick={addPriority}
      >
        <BsPlusLg />
        Add priority
      </button>
    </div>
  );
};

export default Step4;